import Vue from 'vue';
import { Component, Mixins } from 'vue-property-decorator';
import {
    deviceChange,
    isBreakpointDesktopQuery,
    isEscapeKey,
    isMobileQuery,
    MagicConfigMixin,
    modalService,
    ModalSize,
} from '@gig/magic-common';
import type { IModal, IModalOptions } from '@gig/magic-common';

import { RouteName } from '../../routes/routes.constants';
import { ISubscription } from '@gig/magic-message-bus/dist/base-bus';

@Component
export default class ModalContainerComponent extends Mixins(MagicConfigMixin) {
    modals: IModal[] = [];

    private modalListener: null | (() => void) = null;
    private escapeKeyListener: null | ((event: KeyboardEvent) => void) = null;
    private isDesktop: boolean = false;
    private isMobile: boolean = false;

    private deviceSubscription: ISubscription | undefined;

    /**
     * Removes the last modal added
     */
    dismiss(withBackdrop: boolean = false) {
        if (!this.modals.length) {
            return;
        }

        const modal = this.modals[this.modals.length - 1];
        const options = { ...modal.options } || {};

        if (withBackdrop && !options.dismissibleBackdrop) {
            return;
        }

        if (options.dismissible && this.isDesktop) {
            this.clearQueryParameters();
            modalService.close(modal, false);
        }
    }

    created() {
        this.deviceSubscription = deviceChange(async () => {
            this.isDesktop = await isBreakpointDesktopQuery();
            this.isMobile = await isMobileQuery();
        });
    }

    mounted() {
        this.modalsChange();

        this.escapeKeyListener = (event: KeyboardEvent) => {
            if (isEscapeKey(event)) {
                this.dismiss();
            }
        };
        document.addEventListener('keyup', this.escapeKeyListener);
    }

    destroyed() {
        this.deviceSubscription?.unsubscribe();
        if (this.modalListener) {
            this.modalListener();
        }
        if (this.escapeKeyListener) {
            document.removeEventListener('keyup', this.escapeKeyListener);
        }
    }

    close(modal: IModal, params?: any) {
        this.clearQueryParameters();
        modalService.close(modal, true, params);
    }
    goBack(modal: IModal) {
        if (modal.options?.onBack) {
            modal.options.onBack();
            return;
        }

        if (modal.options && typeof modal.options.backArrow === 'string') {
            this.$router.push(modal.options.backArrow);
        } else {
            this.$router.go(-1);
        }
    }

    onBackdropClick() {
        this.dismiss(true);
    }

    setModalOptions(modal: IModal, newOptions: IModalOptions) {
        if (modal) {
            const mergedOptions: IModalOptions = modal.options
                ? { ...modal.options, ...newOptions }
                : newOptions;
            modalService.updateOptions(modal, mergedOptions);
        }
    }

    getActiveModalSize() {
        if (this.modals.length < 1) {
            return null;
        }

        return this.modals[this.modals.length - 1].options?.size;
    }

    getModalContainerCssClass() {
        const classes = [];
        if (this.$route.name === RouteName.GAMEPLAY && this.isMobile) {
            classes.push('container--mobile-gameplay');
        }

        if (this.getActiveModalSize() === ModalSize.BORDERLESS) {
            classes.push('container--borderless');
        }

        return classes.join(' ');
    }

    getModalElementCssClass() {
        const classes = [];

        if (this.getActiveModalSize() === ModalSize.BORDERLESS) {
            classes.push('element--borderless');
        }

        return classes.join(' ');
    }

    getSize(modal: IModal) {
        if (this.$route.name === RouteName.GAMEPLAY && this.isMobile) {
            return ModalSize.MOBILE_OVERLAY;
        }
        return modal.options!.size || ModalSize.DEFAULT;
    }

    private modalsChange() {
        this.modalListener = modalService.onModalsChange((modals, modalId) => {
            this.modals = modals;

            Vue.nextTick(() => {
                this.injectModalContent(modalId);
            });
        });
    }

    private injectModalContent(modalId?: number) {
        const { modals } = this.$refs;
        const modalElements = Array.isArray(modals) ? modals : [modals];

        if (!this.isElements(modalElements) || this.modals.length === 0) {
            return;
        }

        modalElements.forEach((ele) => {
            const modal = this.getModalForElement(ele);
            if (modalId !== undefined && modalId !== modal.id) {
                return;
            }

            if (ele.childNodes.length > 1) {
                ele.removeChild(ele.lastChild!);
            }

            ele.appendChild(modal.body);
        });
    }

    private isElements(
        modalElements: Array<Vue | Element>,
    ): modalElements is Element[] {
        return !(modalElements[0] instanceof Vue) && !!modalElements[0];
    }

    private clearQueryParameters() {
        if (this.$route.query.backTo) {
            this.$router.replace({});
        }
    }

    private getModalForElement(element: Element) {
        // using the index of the element in the `$refs` array is not safe
        // since Vue may reorder elements
        // using the `data-key` attribute, based on the `$key` used in the `v-for`
        // always returns the proper result
        const id = element.getAttribute('data-key');
        return this.modals.find((m) => m.id.toString() === id)!;
    }
}
